<template>
  <section class="dp-layout dingtalk-pc-menu-page">
    <section class="dp-left">
      <DepartmentPane directory-scope="school" :type="1" @tree-node-click="treeNodeClick" />
    </section>
    <section class="dp-right">
      <div class="content-wrapper config-panel">
        <div class="header-compact">
          <div class="header-row header-row--title">
            <div class="header-title-group">
              <span class="page-title">钉钉 PC 学校菜单</span>
              <el-tooltip
                content="按学校配置钉钉 PC 工作台主菜单及其下属子功能。当前子菜单均归属「数据报表」；后续可扩展其他一级主菜单。未配置学校默认全部开启。"
                placement="bottom"
                effect="dark"
              >
                <i class="el-icon-info hint-icon" aria-label="说明"></i>
              </el-tooltip>
            </div>
            <div class="header-actions-inline">
              <template v-if="schoolId">
                <span class="school-label">当前学校</span>
                <el-tag size="small" type="info" effect="plain" class="school-tag">{{ schoolName }}</el-tag>
                <el-tag v-if="!isConfigured" size="small" type="success" effect="plain">未配置（默认全开）</el-tag>
              </template>
              <el-button size="small" type="warning" plain :disabled="!schoolId" :loading="resetLoading" @click="resetDefault">
                恢复默认
              </el-button>
              <el-button type="primary" size="small" :disabled="!schoolId" :loading="saveLoading" @click="saveConfig">
                保存
              </el-button>
            </div>
          </div>
        </div>

        <div v-if="!schoolId" class="empty-holder">
          <el-empty description="请选择左侧学校后配置菜单"></el-empty>
        </div>
        <div v-else v-loading="loading" class="menu-config-body" element-loading-text="加载中...">
          <el-alert
            v-if="isConfigured"
            class="config-hint"
            type="info"
            :closable="false"
            show-icon
          >
            该校已单独配置菜单。工作台按<strong>一级主菜单</strong>展示入口；「数据报表」下的子菜单控制钉钉 PC 侧栏可见项。
          </el-alert>
          <el-alert
            v-else
            class="config-hint"
            type="success"
            :closable="false"
            show-icon
          >
            该校尚未保存过配置，当前为<strong>默认全部开启</strong>。修改后点保存即生效。
          </el-alert>

          <div class="menu-summary-bar">
            <div class="summary-left">
              <span class="summary-label">已开启子菜单</span>
              <span class="summary-count">{{ enabledCount }}</span>
              <span class="summary-total">/ {{ totalCount }}</span>
            </div>
            <div class="summary-actions">
              <el-button type="text" size="small" @click="selectAll">全部开启</el-button>
              <el-divider direction="vertical" />
              <el-button type="text" size="small" @click="selectNone">全部关闭</el-button>
            </div>
          </div>

          <div
            v-for="module in moduleSections"
            :key="module.moduleCode"
            class="module-section"
          >
            <div class="module-section__header">
              <div class="module-section__title-wrap">
                <span class="module-section__badge">一级</span>
                <span class="module-section__title">{{ module.moduleName }}</span>
                <el-tag size="mini" type="primary" effect="plain">
                  {{ moduleEnabledCount(module) }}/{{ moduleItemCount(module) }}
                </el-tag>
              </div>
              <div class="module-section__actions">
                <el-button type="text" size="mini" @click="toggleModule(module, true)">全选</el-button>
                <el-button type="text" size="mini" @click="toggleModule(module, false)">全不选</el-button>
              </div>
            </div>

            <div class="group-grid">
              <div v-for="group in module.groups" :key="module.moduleCode + '-' + group.groupName" class="group-card">
                <div class="group-card__header">
                  <div class="group-card__title-wrap">
                    <span class="group-card__dot" aria-hidden="true"></span>
                    <span class="group-card__title">{{ group.groupName }}</span>
                    <el-tag size="mini" type="info" effect="plain" class="group-card__badge">
                      {{ groupEnabledCount(group) }}/{{ group.items.length }}
                    </el-tag>
                  </div>
                  <div class="group-card__actions">
                    <el-button type="text" size="mini" @click="toggleGroup(group, true)">全选</el-button>
                    <el-button type="text" size="mini" @click="toggleGroup(group, false)">全不选</el-button>
                  </div>
                </div>
                <div class="menu-tile-grid">
                  <el-tooltip
                    v-for="item in group.items"
                    :key="item.menuCode"
                    :content="'路由：' + item.routePath"
                    placement="top"
                    :open-delay="400"
                  >
                    <div
                      class="menu-tile"
                      :class="{ 'menu-tile--active': isEnabled(item.menuCode) }"
                      role="button"
                      tabindex="0"
                      @click="toggleItem(item.menuCode)"
                      @keydown.enter.prevent="toggleItem(item.menuCode)"
                      @keydown.space.prevent="toggleItem(item.menuCode)"
                    >
                      <span class="menu-tile__checkbox" :class="{ 'menu-tile__checkbox--checked': isEnabled(item.menuCode) }">
                        <i v-if="isEnabled(item.menuCode)" class="el-icon-check"></i>
                      </span>
                      <span class="menu-tile__name">{{ item.name }}</span>
                    </div>
                  </el-tooltip>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import DepartmentPane from '@/components/DepartmentPane.vue'
import { getDingTalkPcSchoolMenuBySchool, resetDingTalkPcSchoolMenu, saveDingTalkPcSchoolMenu } from '../../api/modules/misc';
export default {
  name: 'DingTalkPcSchoolMenuPage',
  components: { DepartmentPane },
  data() {
    return {
      schoolId: 0,
      schoolName: '',
      loading: false,
      saveLoading: false,
      resetLoading: false,
      isConfigured: false,
      items: [],
      modules: [],
      enabledCodes: [],
    }
  },
  computed: {
    moduleSections() {
      if (this.modules && this.modules.length) {
        return this.modules.map((module) => ({
          moduleCode: module.moduleCode,
          moduleName: module.moduleName,
          sortOrder: module.sortOrder || 0,
          groups: (module.groups || []).map((group) => ({
            groupName: group.groupName,
            items: group.items || [],
          })),
        }))
      }
      return this.buildModulesFromItems(this.items)
    },
    totalCount() {
      return (this.items || []).length
    },
    enabledCount() {
      return (this.enabledCodes || []).length
    },
  },
  methods: {
    normalize(obj, lowerKey, upperKey, fallback) {
      const v1 = obj ? obj[lowerKey] : undefined
      if (v1 !== undefined && v1 !== null) return v1
      const v2 = obj ? obj[upperKey] : undefined
      if (v2 !== undefined && v2 !== null) return v2
      return fallback
    },
    buildModulesFromItems(items) {
      const moduleMap = new Map()
      ;(items || []).forEach((item) => {
        const moduleCode = item.moduleCode || 'data_report'
        const moduleName = item.moduleName || '数据报表'
        if (!moduleMap.has(moduleCode)) {
          moduleMap.set(moduleCode, {
            moduleCode: moduleCode,
            moduleName: moduleName,
            sortOrder: 0,
            groups: new Map(),
          })
        }
        const module = moduleMap.get(moduleCode)
        const groupName = item.groupName || '其他'
        if (!module.groups.has(groupName)) {
          module.groups.set(groupName, { groupName: groupName, items: [] })
        }
        module.groups.get(groupName).items.push(item)
      })
      return Array.from(moduleMap.values()).map((module) => ({
        moduleCode: module.moduleCode,
        moduleName: module.moduleName,
        sortOrder: module.sortOrder,
        groups: Array.from(module.groups.values()),
      }))
    },
    isEnabled(menuCode) {
      return this.enabledCodes.indexOf(menuCode) !== -1
    },
    toggleItem(menuCode) {
      const idx = this.enabledCodes.indexOf(menuCode)
      if (idx === -1) {
        this.enabledCodes = this.enabledCodes.concat(menuCode)
      } else {
        this.enabledCodes = this.enabledCodes.filter((code) => code !== menuCode)
      }
    },
    selectAll() {
      this.enabledCodes = (this.items || []).map((x) => x.menuCode)
    },
    selectNone() {
      this.enabledCodes = []
    },
    moduleItemCount(module) {
      return (module.groups || []).reduce((sum, group) => sum + (group.items || []).length, 0)
    },
    moduleEnabledCount(module) {
      let count = 0
      ;(module.groups || []).forEach((group) => {
        count += this.groupEnabledCount(group)
      })
      return count
    },
    groupEnabledCount(group) {
      if (!group || !group.items) return 0
      return group.items.filter((item) => this.isEnabled(item.menuCode)).length
    },
    toggleModule(module, enabled) {
      const codes = []
      ;(module.groups || []).forEach((group) => {
        ;(group.items || []).forEach((item) => codes.push(item.menuCode))
      })
      this.toggleCodes(codes, enabled)
    },
    toggleGroup(group, enabled) {
      const codes = (group.items || []).map((x) => x.menuCode)
      this.toggleCodes(codes, enabled)
    },
    toggleCodes(codes, enabled) {
      if (enabled) {
        const merged = this.enabledCodes.slice()
        codes.forEach((code) => {
          if (merged.indexOf(code) === -1) merged.push(code)
        })
        this.enabledCodes = merged
      } else {
        this.enabledCodes = this.enabledCodes.filter((code) => codes.indexOf(code) === -1)
      }
    },
    treeNodeClick(node) {
      this.schoolId = Number(node && node.value ? node.value : 0)
      this.schoolName = (node && node.label) || ''
      if (this.schoolId) {
        this.loadConfig()
      } else {
        this.items = []
        this.modules = []
        this.enabledCodes = []
        this.isConfigured = false
      }
    },
    async loadConfig() {
      this.loading = true
      try {
        const res = await getDingTalkPcSchoolMenuBySchool({ schoolId: this.schoolId })
        if (!res || !res.data || !res.data.success) {
          this.$message.error((res && res.data && res.data.msg) || '加载失败')
          return
        }
        const data = res.data.response || {}
        const rawItems = data.items || data.Items || []
        this.items = rawItems.map((x) => ({
          menuCode: this.normalize(x, 'menuCode', 'MenuCode', ''),
          name: this.normalize(x, 'name', 'Name', ''),
          groupName: this.normalize(x, 'groupName', 'GroupName', '其他'),
          routePath: this.normalize(x, 'routePath', 'RoutePath', ''),
          sortOrder: this.normalize(x, 'sortOrder', 'SortOrder', 0),
          isEnabled: this.normalize(x, 'isEnabled', 'IsEnabled', false),
          moduleCode: this.normalize(x, 'moduleCode', 'ModuleCode', 'data_report'),
          moduleName: this.normalize(x, 'moduleName', 'ModuleName', '数据报表'),
        }))
        const rawModules = data.modules || data.Modules || []
        this.modules = rawModules.map((module) => ({
          moduleCode: this.normalize(module, 'moduleCode', 'ModuleCode', 'data_report'),
          moduleName: this.normalize(module, 'moduleName', 'ModuleName', '数据报表'),
          sortOrder: this.normalize(module, 'sortOrder', 'SortOrder', 0),
          groups: (module.groups || module.Groups || []).map((group) => ({
            groupName: this.normalize(group, 'groupName', 'GroupName', '其他'),
            items: (group.items || group.Items || []).map((x) => ({
              menuCode: this.normalize(x, 'menuCode', 'MenuCode', ''),
              name: this.normalize(x, 'name', 'Name', ''),
              groupName: this.normalize(x, 'groupName', 'GroupName', '其他'),
              routePath: this.normalize(x, 'routePath', 'RoutePath', ''),
              sortOrder: this.normalize(x, 'sortOrder', 'SortOrder', 0),
              isEnabled: this.normalize(x, 'isEnabled', 'IsEnabled', false),
              moduleCode: this.normalize(x, 'moduleCode', 'ModuleCode', 'data_report'),
              moduleName: this.normalize(x, 'moduleName', 'ModuleName', '数据报表'),
            })),
          })),
        }))
        this.isConfigured = !!(data.isConfigured != null ? data.isConfigured : data.IsConfigured)
        this.enabledCodes = this.items.filter((x) => x.isEnabled).map((x) => x.menuCode)
      } catch (e) {
        this.$message.error('加载配置失败')
      } finally {
        this.loading = false
      }
    },
    async saveConfig() {
      if (!this.schoolId) return
      if (!this.items.length) {
        this.$message.warning('暂无菜单项')
        return
      }
      const payload = {
        schoolId: this.schoolId,
        items: this.items.map((item) => ({
          menuCode: item.menuCode,
          isEnabled: this.enabledCodes.includes(item.menuCode),
        })),
      }
      this.saveLoading = true
      try {
        const res = await saveDingTalkPcSchoolMenu(payload)
        if (res && res.data && res.data.success) {
          this.$message.success('保存成功')
          this.isConfigured = true
          await this.loadConfig()
        } else {
          this.$message.error((res && res.data && res.data.msg) || '保存失败')
        }
      } catch (e) {
        this.$message.error('保存失败')
      } finally {
        this.saveLoading = false
      }
    },
    resetDefault() {
      if (!this.schoolId) return
      this.$confirm('恢复默认将删除该校菜单配置，钉钉 PC 将显示全部主菜单及子菜单。是否继续？', '提示', {
        type: 'warning',
      })
        .then(async () => {
          this.resetLoading = true
          try {
            const res = await resetDingTalkPcSchoolMenu({ schoolId: this.schoolId })
            if (res && res.data && res.data.success) {
              this.$message.success('已恢复默认')
              await this.loadConfig()
            } else {
              this.$message.error((res && res.data && res.data.msg) || '操作失败')
            }
          } catch (e) {
            this.$message.error('操作失败')
          } finally {
            this.resetLoading = false
          }
        })
        .catch(() => {})
    },
  },
}
</script>

<style scoped>
/* 滚动由全局 dp-layout/dp-right 统一；此处只保留业务样式 */
.dingtalk-pc-menu-page .dp-right {
  /* 兜底：内部内容区走 flex 列 */
  min-width: 0;
}

.content-wrapper.config-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;
}

.header-compact {
  flex-shrink: 0;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.header-row--title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.header-title-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-title {
  font-size: 15px;
  font-weight: 700;
  color: #303133;
}

.hint-icon {
  color: #909399;
  cursor: help;
}

.hint-icon:hover {
  color: var(--btn-primary, #2563eb);
}

.header-actions-inline {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.school-label {
  font-size: 12px;
  color: #909399;
}

.school-tag {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.empty-holder {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}

.menu-config-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
}

.config-hint {
  margin-bottom: 12px;
}

.menu-summary-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 14px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f8fbff 0%, #f4f7fc 100%);
  border: 1px solid #e4e7ed;
  border-radius: 8px;
}

.summary-left {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.summary-label {
  font-size: 13px;
  color: #606266;
}

.summary-count {
  font-size: 22px;
  font-weight: 700;
  color: var(--btn-primary, #2563eb);
  line-height: 1;
}

.summary-total {
  font-size: 13px;
  color: #909399;
}

.summary-actions {
  display: flex;
  align-items: center;
}

.module-section {
  margin-bottom: 18px;
  padding: 14px 16px 16px;
  background: #fafcff;
  border: 1px solid #dce6f5;
  border-radius: 12px;
}

.module-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4ebf7;
}

.module-section__title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.module-section__badge {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--btn-primary, #2563eb);
  color: #fff;
  font-size: 12px;
  line-height: 1.4;
}

.module-section__title {
  font-size: 16px;
  font-weight: 700;
  color: #303133;
}

.module-section__actions >>> .el-button {
  padding: 0 4px;
}

.group-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.group-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  padding: 12px 14px 14px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.04);
}

.group-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #ebeef5;
}

.group-card__title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.group-card__dot {
  width: 4px;
  height: 14px;
  border-radius: 2px;
  background: var(--btn-primary, #2563eb);
  flex-shrink: 0;
}

.group-card__title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.group-card__badge {
  flex-shrink: 0;
}

.group-card__actions >>> .el-button {
  padding: 0 4px;
}

.menu-tile-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.menu-tile {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fafbfc;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.menu-tile:hover {
  border-color: #c6e2ff;
  background: #f5f9ff;
}

.menu-tile:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.menu-tile--active {
  border-color: var(--btn-primary, #2563eb);
  background: #ecf5ff;
}

.menu-tile__checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  background: #fff;
  flex-shrink: 0;
  font-size: 12px;
  color: #fff;
}

.menu-tile__checkbox--checked {
  border-color: var(--btn-primary, #2563eb);
  background: var(--btn-primary, #2563eb);
}

.menu-tile__name {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  line-height: 1.35;
}

@media (max-width: 900px) {
  .group-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .menu-tile-grid {
    grid-template-columns: 1fr;
  }
}
</style>
